import * as mutations from "../../graphql/mutations";
import SetPackage from "../SetPackage";
import CloseIcon from "@mui/icons-material/Close";
import { get } from "@aws-amplify/api";
import { fetchAuthSession } from "@aws-amplify/auth";
import { apiClient } from "../../helpers/apiClient";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getEnumKeyByValue, useWindowDimensions } from "../../helpers/helpers";
import { fetchAllAssessmentAssignmentsByCustomer } from "../../helpers/apiRequests";
import {
  updateOwnerGroupsForAssessmentAndRelated,
} from "../../helpers/updateOwnerGroups";
import { assessmentValue, packages } from "../../helpers/enums";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Modal,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";

export default function EditCustomerModal({
  open,
  setOpen,
  selectedCustomer,
  allAssessments,
  allUsers,
  fetchCustomers,
  setOpenLoading,
  setNotificationMessage,
  setOpenNotification,
}) {
  const { width } = useWindowDimensions();
  const adminApiName = "AdminQueries";
  const textFieldSx = { width: 400, mb: 2, maxWidth: width - 20 };
  const selectSx = { width: 400, mb: 0, maxWidth: width - 20 };
  const formControlSx = { width: 400, mb: 1, maxWidth: width - 20 };
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 550,
    bgcolor: "background.paper",
    borderRadius: 5,
    boxShadow: 24,
    pb: 4,
    pt: 1,
    maxWidth: width,
  };

  const { t } = useTranslation();
  const emptyCustomer = {
    id: "",
    assessments: [],
    userIds: [],
    trial: assessmentValue.NO_TRIAL,
  };
  const [customer, setCustomer] = useState(emptyCustomer);
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  function resetData() {
    setCustomer(emptyCustomer);
    setSelectedAssessments([]);
    setSelectedUsers([]);
  }

  function handleSelectedCustomerChange(event, index) {
    setCustomer({
      ...customer,
      [index]: event.target.value,
    });
  }

  function handleSelectedAssessmentsChange(assessment) {
    setSelectedAssessments((prevItems) => {
      const existingObj = prevItems.find((item) => item.id === assessment.id);

      if (existingObj) {
        if (existingObj.field === assessment.field) {
          return prevItems.filter((item) => item.id !== assessment.id);
        } else {
          return prevItems.map((item) =>
            item.id === assessment.id
              ? { ...item, package: assessment.package }
              : item
          );
        }
      } else {
        return [...prevItems, assessment];
      }
    });
  }

  function handleSelectedUsersChange(user, isChecked) {
    setSelectedUsers((prev) =>
      isChecked ? [...prev, user] : prev.filter((obj) => obj.id !== user.id)
    );
  }

  function handleSelectedCustomerIsTrialChange(isTrial) {
    setCustomer((prev) => ({ ...prev, trial: isTrial === "Trial" }));
  }

  async function listGroupsOfUser(username) {
    const path = "/listGroupsForUser";

    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();

    const options = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      queryParams: {
        username: username,
      },
    };

    const restOperation = await get({
      apiName: adminApiName,
      path,
      options,
    });

    let parsed;
    try {
      const { body } = await restOperation.response;
      parsed = await body.json();
    } catch {
      parsed = restOperation?.data ?? restOperation;
    }

    return parsed.Groups;
  }

  async function listUniqueGroupsOfUsers(userIds) {
    const groupListsRaw = await Promise.all(userIds.map(listGroupsOfUser));
    return [
      ...new Set(
        groupListsRaw.flatMap((groupArray) =>
          groupArray.map((group) => group.GroupName)
        )
      ),
    ];
  }

  function getAssessmentPackageKey(assessment) {
    return getEnumKeyByValue(packages, assessment.package) || assessment.package;
  }

  function haveDifferentIds(first, second) {
    if (first.length !== second.length) return true;
    return first.some((id) => !second.includes(id));
  }

  function mergeGroups(currentGroups, userGroups) {
    return [...new Set([...(currentGroups ?? []), ...userGroups])];
  }

  function shouldPropagateGroups(assessment, userGroups) {
    const currentGroups = assessment.ownerGroups ?? [];
    const mergedGroups = mergeGroups(currentGroups, userGroups);
    return mergedGroups.some((group) => !currentGroups.includes(group));
  }

  function refreshCustomersInBackground() {
    Promise.resolve(fetchCustomers({ showLoading: false })).catch((error) => {
      console.error(error);
    });
  }

  function propagateOwnerGroupsInBackground(assessments, userGroups) {
    const affectedAssessments = (assessments || []).filter((assessment) =>
      shouldPropagateGroups(assessment, userGroups)
    );

    if (affectedAssessments.length === 0) return;

    Promise.all(
      affectedAssessments.map((assessment) =>
        updateOwnerGroupsForAssessmentAndRelated(
          assessment.id,
          mergeGroups(assessment.ownerGroups, userGroups)
        )
      )
    ).catch((error) => {
      console.error("Failed to propagate owner groups", error);
    });
  }

  async function createCustomer() {
    try {
      setOpen(false);
      setOpenLoading(true);
      const userIds = selectedUsers.map((item) => item.id);
      const uniqueGroups = await listUniqueGroupsOfUsers(userIds);

      const response = await apiClient.graphql({
        query: mutations.createCustomer,
        variables: {
          input: {
            name: customer.name,
            active: false,
            trial: customer.trial === true,
            usernames: userIds,
            ownerGroups: uniqueGroups,
          },
        },
      });

      const createdCustomer = response.data.createCustomer;
      const customerId = createdCustomer.id;

      await Promise.all(
        selectedAssessments.map(async (selectedAssessment) => {
          await apiClient.graphql({
            query: mutations.createAssessmentAssignment,
            variables: {
              input: {
                customerID: customerId,
                assessmentID: selectedAssessment.id,
                version: 1,
                package: getAssessmentPackageKey(selectedAssessment),
                ownerGroups: uniqueGroups,
              },
            },
          });
        })
      );
      setNotificationMessage(t("clientAdded"));
      propagateOwnerGroupsInBackground(selectedAssessments, uniqueGroups);
      refreshCustomersInBackground();
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenLoading(false);
    setOpenNotification(true);
    resetData();
  }

  async function updateCustomer() {
    setOpenLoading(true);
    try {
      const userIds = selectedUsers.map((item) => item.id);
      const previousUserIds = (selectedCustomer.userIds || []).map(
        (item) => item.id
      );
      const usersChanged = haveDifferentIds(previousUserIds, userIds);

      const [uniqueUserGroups, assessmentAssignments] = await Promise.all([
        listUniqueGroupsOfUsers(userIds),
        fetchAllAssessmentAssignmentsByCustomer(customer.id),
      ]);

      await apiClient.graphql({
        query: mutations.updateCustomer,
        variables: {
          input: {
            id: customer.id,
            name: customer.name,
            trial: customer.trial,
            usernames: userIds,
            ownerGroups: uniqueUserGroups,
          },
        },
      });

      const currentMap = new Map(
        assessmentAssignments.map((item) => [item.assessmentID, item])
      );
      const incomingMap = new Map(
        selectedAssessments.map((item) => [item.id, item])
      );

      const toCreate = selectedAssessments.filter(
        (inc) => !currentMap.has(inc.id)
      );

      const toUpdate = selectedAssessments.filter((inc) => {
        const match = currentMap.get(inc.id);
        const incomingPackage = getAssessmentPackageKey(inc);
        const incomingVersion = inc.version || match?.version || 1;
        return (
          match &&
          (incomingPackage !== match.package ||
            incomingVersion !== match.version ||
            usersChanged)
        );
      });

      const toDelete = assessmentAssignments.filter(
        (cur) => !incomingMap.has(cur.assessmentID)
      );

      await Promise.all(
        toCreate.map(async (assessment) => {
          await apiClient.graphql({
            query: mutations.createAssessmentAssignment,
            variables: {
              input: {
                customerID: customer.id,
                assessmentID: assessment.id,
                version: assessment.version || 1,
                package: getAssessmentPackageKey(assessment),
                ownerGroups: uniqueUserGroups,
              },
            },
          });
        })
      );

      await Promise.all(
        toUpdate.map(async (assessment) => {
          const assigned = assessmentAssignments.find(
            (assignment) => assignment.assessmentID === assessment.id
          );

          await apiClient.graphql({
            query: mutations.updateAssessmentAssignment,
            variables: {
              input: {
                id: assigned.id,
                customerID: customer.id,
                assessmentID: assessment.id,
                version: assessment.version || assigned.version,
                package: getAssessmentPackageKey(assessment),
                ownerGroups: uniqueUserGroups,
              },
            },
          });
        })
      );

      await Promise.all(
        toDelete.map(async (assessment) => {
          await apiClient.graphql({
            query: mutations.deleteAssessmentAssignment,
            variables: {
              input: {
                id: assessment.id,
              },
            },
          });
        })
      );

      const createdAssessmentIds = new Set(toCreate.map((item) => item.id));
      const assessmentsToPropagate = selectedAssessments.filter(
        (assessment) => usersChanged || createdAssessmentIds.has(assessment.id)
      );

      propagateOwnerGroupsInBackground(
        assessmentsToPropagate,
        uniqueUserGroups
      );
      refreshCustomersInBackground();
      setNotificationMessage(t("customerUpdated"));
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenLoading(false);
    setOpenNotification(true);
    resetData();
    setOpen(false);
  }

  useEffect(() => {
    if (open) {
      if (selectedCustomer.id !== "") {
        setCustomer(selectedCustomer);
        setSelectedAssessments(selectedCustomer.assessments);
        setSelectedUsers(selectedCustomer.userIds);
      } else {
        resetData();
      }
      setCustomer(selectedCustomer);
      setSelectedAssessments(selectedCustomer.assessments);
      setSelectedUsers(selectedCustomer.userIds);
    } else {
      resetData();
    }
  }, [open]);

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableAutoFocus={true}
    >
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
          }}
        >
          <IconButton
            color="secondary"
            sx={{ mr: 1 }}
            onClick={() => {
              resetData();
              setOpen(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: 1,
          }}
        >
          <Typography sx={{ fontSize: 18, userSelect: "none" }}>
            {selectedCustomer.id === ""
              ? t("addCustomer")
              : t("updateCustomer")}
          </Typography>
          <Divider flexItem sx={{ mb: 4 }} />
          <TextField
            required
            color="secondary"
            InputLabelProps={{
              shrink: true,
            }}
            label={t("name")}
            variant="outlined"
            size="small"
            sx={textFieldSx}
            value={customer.name}
            onChange={(event) => {
              handleSelectedCustomerChange(event, "name");
            }}
          />
          <FormControl sx={formControlSx} fullWidth size="small">
            <InputLabel shrink={true}>Assessments</InputLabel>
            <Select
              notched={true}
              multiple
              sx={selectSx}
              value={selectedAssessments}
              input={<OutlinedInput label="Assessments" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((assessment, index) => {
                    const label =
                      assessment.name +
                      " | " +
                      assessment.language +
                      " (" +
                      assessment.package +
                      ")";
                    return <Chip key={index} label={label} />;
                  })}
                </Box>
              )}
            >
              {allAssessments.map((assessment, index) => {
                const select = selectedAssessments.find(
                  (selected) => selected.id === assessment.id
                );
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      my: 1,
                      borderBottom: 1,
                      pb: 1,
                      borderColor: "#cccccc",
                    }}
                  >
                    {select ? (
                      <SetPackage
                        buttonVariant={
                          select.package === packages.BASIC
                            ? "contained"
                            : "outlined"
                        }
                        buttonBgColor={
                          select.package === packages.PREMIUM
                            ? "#FFD700"
                            : "#ffffff"
                        }
                        assessment={assessment}
                        selectedAssessmentChange={
                          handleSelectedAssessmentsChange
                        }
                      />
                    ) : (
                      <SetPackage
                        assessment={assessment}
                        selectedAssessmentChange={
                          handleSelectedAssessmentsChange
                        }
                      />
                    )}
                    <Typography sx={{ mr: 2 }}>
                      {assessment.name + " | " + assessment.language}
                    </Typography>
                  </Box>
                );
              })}
            </Select>
          </FormControl>
          <FormControl sx={formControlSx} fullWidth size="small">
            <InputLabel shrink={true}>Users</InputLabel>
            <Select
              notched={true}
              multiple
              sx={selectSx}
              value={selectedUsers}
              input={<OutlinedInput label="Assessments" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((user) => {
                    return <Chip key={user.id} label={user.email} />;
                  })}
                </Box>
              )}
            >
              {allUsers.map((user) => {
                const select = selectedUsers.find(
                  (selected) => selected.id === user.id
                );
                return (
                  <Box
                    key={user.id}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      my: 1,
                      borderBottom: 1,
                      pb: 1,
                      borderColor: "#cccccc",
                    }}
                  >
                    <Checkbox
                      checked={select}
                      onClick={() => handleSelectedUsersChange(user, !select)}
                    />
                    <Typography sx={{ mr: 2 }}>{user.email}</Typography>
                  </Box>
                );
              })}
            </Select>
          </FormControl>
          <FormControl sx={formControlSx}>
            <InputLabel shrink={true}>Trial</InputLabel>
            <Select
              required
              notched={true}
              label={"Version"}
              size="small"
              sx={selectSx}
              value={
                customer.trial
                  ? assessmentValue.TRIAL
                  : assessmentValue.NO_TRIAL
              }
              onChange={(event) =>
                handleSelectedCustomerIsTrialChange(event.target.value)
              }
            >
              {Object.values(assessmentValue).map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            sx={{ mt: 1 }}
            color="secondary"
            variant="contained"
            size="small"
            onClick={() => {
              if (selectedCustomer.id === "") {
                createCustomer();
              } else {
                updateCustomer();
              }
            }}
          >
            {selectedCustomer.id === ""
              ? t("addCustomer")
              : t("updateCustomer")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
